import { type AccountMembersOverviewDto, type ProfileDto } from '../../services/profile.api';
import { getEuCountryLabel, renderEuCountryOptions } from '../../services/eu-countries';
import { t } from '../../services/i18n';
import { esc, initials } from './profile-shared';

export class RcProfileTabProfile extends HTMLElement {
  profile: ProfileDto | null = null;
  accountMembers: AccountMembersOverviewDto | null = null;
  userName = '';
  userEmail = '';
  providerLabel = '';
  error = '';
  isLoading = false;
  isSaving = false;
  isSwitchingAccountMode = false;
  accountModeError = '';
  isManagingAccountMembers = false;
  accountMembersError = '';

  render(): void {
    if (this.isLoading) {
      this.innerHTML = `<div class="card profile-card"><div class="empty-state">${t('profile.loading')}</div></div>`;
      return;
    }

    const profile = this.profile ?? {
      email: this.userEmail,
      displayName: this.userName,
      providerDisplayName: this.userName,
      customDisplayName: null,
      accountMode: 'landlord' as const,
      firstName: '',
      lastName: '',
      isBusinessAccount: false,
      companyName: '',
      vatNumber: '',
      street: '',
      houseNumber: '',
      postalCode: '',
      city: '',
      country: '',
      billingStreet: '',
      billingHouseNumber: '',
      billingPostalCode: '',
      billingCity: '',
      billingCountry: '',
    };
    const isBusinessAccount = !!profile.isBusinessAccount;
    const allowedCountryCodes = profile.allowedCountryCodes || [];
    const displayName = profile.displayName || profile.providerDisplayName || this.userName;
    const isTenantMode = profile.accountMode === 'tenant';
    const canManageAccount = profile.canManageAccount !== false;

    this.innerHTML = `
      <div class="card profile-card">
        <div class="profile-main">
          <div class="profile-summary">
            <div class="profile-avatar">${esc(initials(displayName))}</div>
            <div class="profile-summary-main">
              <h2 class="profile-summary-name">${esc(displayName)}</h2>
              <div class="profile-summary-meta">
                <span class="profile-summary-chip">${esc(profile.email || this.userEmail)}</span>
                <span class="profile-summary-chip">${esc(this.providerLabel)}</span>
              </div>
            </div>
          </div>

          <form id="profile-address-form" class="profile-address-form" novalidate>
            <section class="profile-form-section">
              <div class="profile-section-head">
                <h2>${t('common.name')}</h2>
              </div>
              ${this.error ? `<div class="form-error">${esc(this.error)}</div>` : ''}
              <div class="form-row">
                <div class="form-group">
                  <label for="profile-first-name">${t('tenantOnboarding.firstName')}</label>
                  <input id="profile-first-name" name="firstName" type="text" value="${esc(profile.firstName || '')}" autocomplete="given-name" ${canManageAccount ? '' : 'disabled'}>
                </div>
                <div class="form-group">
                  <label for="profile-last-name">${t('tenantOnboarding.lastName')}</label>
                  <input id="profile-last-name" name="lastName" type="text" value="${esc(profile.lastName || '')}" autocomplete="family-name" ${canManageAccount ? '' : 'disabled'}>
                </div>
              </div>
            </section>
            <section class="profile-form-section">
              <div class="profile-section-head">
                <h2>${t('profile.addressDetails')}</h2>
              </div>
              <div class="form-group">
                <label for="profile-account-type">${t('profile.accountType')}</label>
                <select id="profile-account-type" name="accountType" ${canManageAccount ? '' : 'disabled'}>
                  <option value="private" ${isBusinessAccount ? '' : 'selected'}>${t('profile.accountType.private')}</option>
                  <option value="business" ${isBusinessAccount ? 'selected' : ''}>${t('profile.accountType.business')}</option>
                </select>
              </div>
              <div class="form-group profile-business-only"${isBusinessAccount ? '' : ' hidden'}>
                <label for="profile-company-name">${t('profile.companyName')} <span class="required">*</span></label>
                <input id="profile-company-name" name="companyName" type="text" value="${esc(profile.companyName || '')}" autocomplete="organization" ${canManageAccount ? '' : 'disabled'}>
              </div>
              <div class="form-group profile-business-only"${isBusinessAccount ? '' : ' hidden'}>
                <label for="profile-vat-number">${t('profile.vatNumber')}</label>
                <input id="profile-vat-number" name="vatNumber" type="text" value="${esc(profile.vatNumber || '')}" autocomplete="off" placeholder="BE0123456789" ${canManageAccount ? '' : 'disabled'}>
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label for="profile-street">${t('profile.street')}</label>
                  <input id="profile-street" name="street" type="text" value="${esc(profile.street || '')}" autocomplete="address-line1" ${canManageAccount ? '' : 'disabled'}>
                </div>
                <div class="form-group profile-house-number-group">
                  <label for="profile-house-number">${t('profile.houseNumber')}</label>
                  <input id="profile-house-number" name="houseNumber" type="text" value="${esc(profile.houseNumber || '')}" autocomplete="address-line2" ${canManageAccount ? '' : 'disabled'}>
                </div>
              </div>
              <div class="form-row">
                <div class="form-group profile-postal-code-group">
                  <label for="profile-postal-code">${t('profile.postalCode')}</label>
                  <input id="profile-postal-code" name="postalCode" type="text" value="${esc(profile.postalCode || '')}" autocomplete="postal-code" ${canManageAccount ? '' : 'disabled'}>
                </div>
                <div class="form-group">
                  <label for="profile-city">${t('profile.city')}</label>
                  <input id="profile-city" name="city" type="text" value="${esc(profile.city || '')}" autocomplete="address-level2" ${canManageAccount ? '' : 'disabled'}>
                </div>
              </div>
              <div class="form-group">
                <label for="profile-country">${t('profile.country')}</label>
                <input id="profile-country" name="country" type="text" list="profile-country-options" value="${esc(getEuCountryLabel(profile.country, allowedCountryCodes))}" autocomplete="country-name" placeholder="${t('profile.countryPlaceholder')}" ${canManageAccount ? '' : 'disabled'}>
                <datalist id="profile-country-options">${renderEuCountryOptions(profile.country, allowedCountryCodes)}</datalist>
              </div>
              <div class="profile-section-head profile-business-only"${isBusinessAccount ? '' : ' hidden'}>
                <h2>${t('profile.billingAddress')}</h2>
              </div>
              <div class="form-row profile-business-only"${isBusinessAccount ? '' : ' hidden'}>
                <div class="form-group">
                  <label for="profile-billing-street">${t('profile.street')}</label>
                  <input id="profile-billing-street" name="billingStreet" type="text" value="${esc(profile.billingStreet || '')}" autocomplete="address-line1" ${canManageAccount ? '' : 'disabled'}>
                </div>
                <div class="form-group profile-house-number-group">
                  <label for="profile-billing-house-number">${t('profile.houseNumber')}</label>
                  <input id="profile-billing-house-number" name="billingHouseNumber" type="text" value="${esc(profile.billingHouseNumber || '')}" autocomplete="address-line2" ${canManageAccount ? '' : 'disabled'}>
                </div>
              </div>
              <div class="form-row profile-business-only"${isBusinessAccount ? '' : ' hidden'}>
                <div class="form-group profile-postal-code-group">
                  <label for="profile-billing-postal-code">${t('profile.postalCode')}</label>
                  <input id="profile-billing-postal-code" name="billingPostalCode" type="text" value="${esc(profile.billingPostalCode || '')}" autocomplete="postal-code" ${canManageAccount ? '' : 'disabled'}>
                </div>
                <div class="form-group">
                  <label for="profile-billing-city">${t('profile.city')}</label>
                  <input id="profile-billing-city" name="billingCity" type="text" value="${esc(profile.billingCity || '')}" autocomplete="address-level2" ${canManageAccount ? '' : 'disabled'}>
                </div>
              </div>
              <div class="form-group profile-business-only"${isBusinessAccount ? '' : ' hidden'}>
                <label for="profile-billing-country">${t('profile.country')}</label>
                <input id="profile-billing-country" name="billingCountry" type="text" list="profile-billing-country-options" value="${esc(getEuCountryLabel(profile.billingCountry, allowedCountryCodes))}" autocomplete="country-name" placeholder="${t('profile.countryPlaceholder')}" ${canManageAccount ? '' : 'disabled'}>
                <datalist id="profile-billing-country-options">${renderEuCountryOptions(profile.billingCountry, allowedCountryCodes)}</datalist>
              </div>
            </section>
            ${canManageAccount ? `
              <div class="profile-actions">
                <button type="submit" class="btn btn-primary" ${this.isSaving ? 'disabled' : ''}>${this.isSaving ? t('common.saving') : t('profile.saveButton')}</button>
              </div>
            ` : `<div class="billing-warning">${t('profile.accountMembers.readerProfileNotice')}</div>`}
          </form>
          <section id="account-mode-section" class="profile-form-section">
            <div class="profile-section-head">
              <h2>${t('profile.accountModeTitle')}</h2>
              <p>${t(isTenantMode ? 'profile.accountModeTenantDescription' : 'profile.accountModeLandlordDescription')}</p>
            </div>
            ${this.accountModeError ? `<div class="form-error">${esc(this.accountModeError)}</div>` : ''}
            <div class="profile-actions">
              ${isTenantMode
                ? `<button type="button" class="btn btn-primary" data-switch-account-mode="landlord" ${(this.isSwitchingAccountMode || !canManageAccount) ? 'disabled' : ''}>${this.isSwitchingAccountMode ? t('common.saving') : t('profile.switchToLandlord')}</button>`
                : `<button type="button" class="btn btn-outline" data-switch-account-mode="tenant" ${(this.isSwitchingAccountMode || !canManageAccount) ? 'disabled' : ''}>${this.isSwitchingAccountMode ? t('common.saving') : t('profile.switchToTenant')}</button>`}
            </div>
          </section>
          ${this.renderAccountMembers(canManageAccount)}
        </div>
      </div>
    `;

    this.querySelector<HTMLFormElement>('#profile-address-form')?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.dispatchEvent(new CustomEvent('profile-save', {
        bubbles: true,
        detail: { form: event.currentTarget as HTMLFormElement },
      }));
    });

    this.querySelector<HTMLSelectElement>('#profile-account-type')?.addEventListener('change', (event) => {
      const isBusiness = (event.currentTarget as HTMLSelectElement).value === 'business';
      this.querySelectorAll<HTMLElement>('.profile-business-only').forEach((element) => {
        element.hidden = !isBusiness;
      });
      this.dispatchEvent(new CustomEvent('profile-account-type-change', {
        bubbles: true,
        detail: { isBusinessAccount: isBusiness },
      }));
    });

    this.querySelector<HTMLElement>('[data-switch-account-mode]')?.addEventListener('click', (event) => {
      const accountMode = (event.currentTarget as HTMLElement).getAttribute('data-switch-account-mode');
      if (!accountMode) {
        return;
      }

      this.dispatchEvent(new CustomEvent('profile-switch-account-mode', {
        bubbles: true,
        detail: { accountMode },
      }));
    });

    this.querySelector<HTMLFormElement>('#account-member-invite-form')?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.dispatchEvent(new CustomEvent('profile-invite-member', {
        bubbles: true,
        detail: { form: event.currentTarget as HTMLFormElement },
      }));
    });

    this.querySelectorAll<HTMLElement>('[data-remove-member]').forEach((button) => {
      button.addEventListener('click', () => {
        const memberUserId = button.getAttribute('data-remove-member');
        if (!memberUserId) {
          return;
        }

        this.dispatchEvent(new CustomEvent('profile-remove-member', {
          bubbles: true,
          detail: { memberUserId },
        }));
      });
    });
  }

  private renderAccountMembers(canManageAccount: boolean): string {
    const accountMembers = this.accountMembers;
    const members = accountMembers?.members ?? [];
    const pendingInvites = accountMembers?.pendingInvites ?? [];

    return `
      <section class="profile-form-section">
        <div class="profile-section-head">
          <h2>${t('profile.accountMembers.title')}</h2>
          <p>${t('profile.accountMembers.description')}</p>
        </div>
        ${this.accountMembersError ? `<div class="form-error">${esc(this.accountMembersError)}</div>` : ''}
        <div class="profile-account-members-list">
          ${members.map((member) => `
            <article class="profile-account-member-card">
              <div>
                <strong>${esc(member.displayName)}</strong>
                <div class="billing-invoice-meta">${esc(member.email)}</div>
                <div class="billing-invoice-meta">${esc(t(`profile.accountMembers.role.${member.accessRole}`))}${member.isCurrentUser ? ` • ${esc(t('profile.accountMembers.you'))}` : ''}</div>
              </div>
              ${canManageAccount && !member.isOwner ? `
                <button type="button" class="btn btn-outline" data-remove-member="${esc(member.id)}" ${this.isManagingAccountMembers ? 'disabled' : ''}>${t('profile.accountMembers.removeButton')}</button>
              ` : ''}
            </article>
          `).join('')}
          ${!members.length ? `<div class="empty-state">${t('profile.accountMembers.empty')}</div>` : ''}
        </div>
        ${pendingInvites.length ? `
          <div class="profile-account-pending">
            <strong>${t('profile.accountMembers.pendingTitle')}</strong>
            ${pendingInvites.map((invite) => `
              <div class="billing-invoice-meta">${esc(invite.email)} • ${esc(t(`profile.accountMembers.role.${invite.accessRole}`))}</div>
            `).join('')}
          </div>
        ` : ''}
        ${canManageAccount ? `
          <form id="account-member-invite-form" class="profile-address-form" novalidate>
            <div class="form-row">
              <div class="form-group">
                <label for="account-member-email">${t('profile.accountMembers.emailLabel')}</label>
                <input id="account-member-email" name="inviteEmail" type="email" autocomplete="email" ${this.isManagingAccountMembers ? 'disabled' : ''}>
              </div>
              <div class="form-group">
                <label for="account-member-role">${t('profile.accountMembers.roleLabel')}</label>
                <select id="account-member-role" name="accessRole" ${this.isManagingAccountMembers ? 'disabled' : ''}>
                  <option value="manager">${t('profile.accountMembers.role.manager')}</option>
                  <option value="reader">${t('profile.accountMembers.role.reader')}</option>
                </select>
              </div>
            </div>
            <div class="profile-actions">
              <button type="submit" class="btn btn-primary" ${this.isManagingAccountMembers ? 'disabled' : ''}>${this.isManagingAccountMembers ? t('common.saving') : t('profile.accountMembers.inviteButton')}</button>
            </div>
          </form>
        ` : ''}
      </section>
    `;
  }
}

if (!customElements.get('rc-profile-tab-profile')) {
  customElements.define('rc-profile-tab-profile', RcProfileTabProfile);
}
